import { useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { ChevronsUpDown } from 'lucide-react';
import clsx from 'clsx';

import { isBE } from '@/react/portainer/feature-flags/feature-flags.service';
import { useSystemInfo } from '@/react/portainer/system/useSystemInfo';

import { ButtonWithRef } from '@@/buttons/Button';
import { Link } from '@@/Link';

import { useWhiteLabel } from '../portainer/feature-flags/useWhiteLabel';

import fullLogoBE from './portainer_logo-BE.svg';
import fullLogoCE from './portainer_logo-CE.svg';
import portainerIcon from './portainer-p-icon-white.svg';
import { useSidebarState } from './useSidebarState';
import { AddonSwitcher } from './AddonSwitcher/AddonSwitcher';
import styles from './Header.module.css';

interface Props {
  logo?: string;
}

export function Header({ logo: customLogo }: Props) {
  const { isOpen } = useSidebarState();
  const { data: whitelabel } = useWhiteLabel();
  const { data: systemInfo } = useSystemInfo();
  const addonsAvailable = systemInfo?.addonsAvailable ?? false;
  const [switcherOpen, setSwitcherOpen] = useState(false);

  return (
    <div
      className={clsx('flex w-full flex-wrap', {
        'justify-center pr-5': !isOpen,
      })}
    >
      <div className="relative flex justify-center gap-2">
        {((!addonsAvailable && !isOpen) || isOpen) && (
          <Link
            to="portainer.home"
            data-cy="portainerSidebar-homeImage"
            className="text-2xl text-white no-underline hover:text-white hover:no-underline focus:text-white focus:no-underline focus:outline-none"
          >
            <Logo customLogo={customLogo} isOpen={isOpen} />
          </Link>
        )}
        {addonsAvailable && (
          <Popover.Root open={switcherOpen} onOpenChange={setSwitcherOpen}>
            <Popover.Trigger asChild>
              <ButtonWithRef
                className={clsx(
                  'flex h-[26px] w-[26px] rounded bg-transparent px-1 py-0 transition-colors duration-200 th-highcontrast:!bg-transparent th-highcontrast:!text-white th-dark:!bg-transparent th-dark:!text-white',
                  {
                    'border-0 th-highcontrast:border-none th-dark:border-none':
                      !isOpen,
                    'border !border-gray-6': isOpen,
                  }
                )}
                data-cy="portainerSidebar-addonSwitcher"
                aria-label="Toggle add-on switcher"
              >
                {isOpen ? (
                  <ChevronsUpDown style={{ width: '16px', height: '16px' }} />
                ) : (
                  <Logo customLogo={customLogo} isOpen={isOpen} />
                )}
              </ButtonWithRef>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content
                className="z-50"
                side="bottom"
                align="start"
                sideOffset={12}
                // 228px left to align with the Portainer logo
                alignOffset={isOpen ? -228 : 0}
              >
                <AddonSwitcher />
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        )}
      </div>
      {isOpen && customLogo && !whitelabel && (
        <div
          className={clsx(
            'space-x-1 pt-3 text-[9.4px] uppercase tracking-[.28em]',
            'text-gray-3',
            'th-dark:text-gray-warm-6'
          )}
        >
          <span className="font-medium">Powered by</span>
          <span className="font-semibold">
            {isBE ? (
              'portainer business'
            ) : (
              <a
                href="https://www.portainer.io/install-BE-now"
                className={clsx(
                  'hover:underline',
                  'text-blue-6 hover:text-blue-8',
                  'th-dark:text-blue-7 th-dark:hover:text-blue-9'
                )}
              >
                portainer community
              </a>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

function getLogo(isOpen: boolean, customLogo?: string) {
  if (customLogo) {
    return customLogo;
  }

  if (!isOpen) {
    return portainerIcon;
  }

  return isBE ? fullLogoBE : fullLogoCE;
}

function Logo({
  customLogo,
  isOpen,
}: {
  customLogo?: string;
  isOpen: boolean;
}) {
  const logo = getLogo(isOpen, customLogo);

  return (
    <img
      src={logo}
      className={clsx('img-responsive', styles.logo, {
        '!max-h-[27px]': !isOpen,
      })}
      alt="Logo"
    />
  );
}
